import type { AiBlock, AiBlockData, Flow, FlowPathCondition } from '@prisma/client';
import { AiBlockType } from '@prisma/client';
import ELK from 'elkjs/lib/elk.bundled.js';
import { produce } from 'immer';
import { Edge, getConnectedEdges, getIncomers, getOutgoers, Node, Position } from 'reactflow';
import { v4 as UUID } from 'uuid';

import { DYNAMIC_EDITOR_TAG_EXTENSION_NAME } from '@/components/dynamic-input/constants';
import { integrations as IntegrationsList } from '@/levity/integrations/core';
import { capitalize } from '@/utils/text-transformations';

import { getOne } from './db';
import type {
  AiBlockNode,
  AiBlockNodeData,
  App,
  AppNode,
  AppNodeData,
  CreateDynamicInputContentParams,
  DynamicTagParams,
  FlowEdge,
  FlowNode,
  FlowNodesChangeset,
  FlowNodeWithoutConnection,
  FlowTreeNode,
  NewNodeSource,
  NodesChangeset,
  PathNode,
} from './types';
import { AiBlockNodeConnectionType, AppNodeType, EdgeType, FlowNodeType } from './types';

export const BASE_URL = '/flows';

export const urls = {
  overview: BASE_URL,
  single: (id: string) => `${BASE_URL}/${id}/manage`,
};

export const appNodeDefaultSize = (node: AppNode) => {
  if (node.data.type === 'trigger') {
    return { width: 216, height: 92 };
  }
  if (node.data.type === 'target') {
    return { width: 216, height: 92 };
  }
  return { width: 216, height: 92 };
};

export const aiBlockNodeDefaultSize = (node: AiBlockNode) => {
  switch (node.data.connectionType) {
    case AiBlockNodeConnectionType.LINEAR:
      return { width: 448, height: 152 };
    case AiBlockNodeConnectionType.BRANCH:
      return { width: 580, height: 208 };
    default:
      return { width: 180, height: 110 };
  }
};

export const nodeDefaultSize = (node: Node) => {
  switch (node.type) {
    case FlowNodeType.APP:
      return appNodeDefaultSize(node as AppNode);
    case FlowNodeType.AI_BLOCK:
      return aiBlockNodeDefaultSize(node as AiBlockNode);
    case FlowNodeType.PATH:
      return appNodeDefaultSize(node as AppNode);
    case FlowNodeType.WAIT:
      return appNodeDefaultSize(node as AppNode);
    default:
      throw new Error('Node type not recognized');
  }
};

const elk = new ELK();
export const tidyNodes = async (nodes: Node[], edges: Edge[]): Promise<Node[]> => {
  const graph = {
    id: 'root',
    layoutOptions: {
      'elk.algorithm': 'mrtree',
      'elk.direction': 'RIGHT',
      'elk.spacing.nodeNode': '60',
    },
    children: nodes.map((node) => ({
      ...node,
      width: node.width ?? nodeDefaultSize(node).width,
      height: node.height ?? nodeDefaultSize(node).height,
      targetPosition: Position.Left,
      sourcePosition: Position.Right,
    })),
    edges: edges.map((edge) => ({
      ...edge,
      sources: [edge.source],
      targets: [edge.target],
    })),
  };

  return elk.layout(graph).then((layout) => {
    if (!layout.children) {
      return nodes;
    }
    return layout.children.map((node) => ({
      ...node,
      position: { x: node.x!, y: node.y! },
    }));
  });
};

export const isAppNode = (type: FlowNodeType) => type === FlowNodeType.APP;

export const hasTriggerApp = (nodes: Node[]) =>
  nodes.some((node) => node.type === FlowNodeType.APP && (node as Node<AppNodeData>).data.type === 'trigger');

export const hasTargetApp = (nodes: Node[]) =>
  nodes.some((node) => node.type === FlowNodeType.APP && (node as Node<AppNodeData>).data.type === 'target');

export const appNodeConnectionType = (nodes: Node[]): AppNodeType =>
  !hasTriggerApp(nodes) ? AppNodeType.TRIGGER : AppNodeType.TARGET;

export const appNode = (data: AppNodeData): Node<AppNodeData> => ({
  id: UUID(),
  type: FlowNodeType.APP,
  position: { x: 0, y: 0 },
  data,
});

export const aiBlockNode = (data: AiBlockNodeData): AiBlockNode => ({
  id: UUID(),
  type: FlowNodeType.AI_BLOCK,
  position: { x: 0, y: 0 },
  data,
});

export const pathNode = (): PathNode => ({
  id: UUID(),
  type: FlowNodeType.PATH,
  position: { x: 0, y: 0 },
  data: {},
});

export const waitNode = (): Node => ({
  id: UUID(),
  type: FlowNodeType.WAIT,
  position: { x: 0, y: 0 },
  data: {},
});

export const nodeFromDBtoReactFlow = (node: FlowNode): Node => ({
  id: node.id,
  type: node.type,
  position: { x: 0, y: 0 },
  data: node.metadata,
});

export const nodesFromDBtoReactFlow = (nodes: FlowNode[]): Node[] => nodes.map(nodeFromDBtoReactFlow);

export const edgeFromDBtoReactFlow = (edge: FlowEdge): Edge => ({
  id: edge.id,
  source: edge.sourceId,
  target: edge.targetId,
  sourceHandle: edge.sourceHandle,
  targetHandle: edge.targetHandle,
  type: EdgeType.DEFAULT,
});

export const edgesFromDBtoReactFlow = (edges: FlowEdge[]): Edge[] => edges.map(edgeFromDBtoReactFlow);

export const nodeFromReactFlowToDB =
  (versionId: string) =>
  (node: Node): FlowNodeWithoutConnection => ({
    id: node.id,
    type: node.type as FlowNodeType,
    versionId,
    metadata: node.data ?? {},
  });

export const edgeFromReactFlowToDB =
  (versionId: string) =>
  (edge: Edge): FlowEdge => ({
    id: edge.id,
    sourceId: edge.source as string,
    targetId: edge.target as string,
    sourceHandle: edge.sourceHandle as string,
    targetHandle: edge.targetHandle as string,
    versionId,
  });

// Purpose: Function inserts a new node of type AI_BLOCK or APP
// Restriction: Does not support insertion of PATH type nodes. Use 'insertPathNode' instead.
export const insertNode = async ({
  node,
  source,
  ...rest
}: {
  node: AppNode | AiBlockNode;
  source?: NewNodeSource;
  nodes: Node[];
  edges: Edge[];
}) => {
  const nodes = [...rest.nodes];
  const edges = [...rest.edges];

  const operations: NodesChangeset = {
    nodes: { add: [] },
    edges: { add: [], remove: [] },
  };

  if (!source) {
    operations.nodes.add!.push(node);
    return { nodes: nodes.concat(node), edges, operations };
  }

  // Check if source is already connected to another node
  const edgesConnectedToSource = edges.filter((edge) => edge.source === source.nodeId);
  const isConnected = edgesConnectedToSource.length > 0;

  // Scenario 1: Source is not connected. Add new node as a child.
  if (!isConnected) {
    const newSourceEdge: Edge = {
      id: UUID(),
      source: source.nodeId,
      sourceHandle: source.handleId,
      target: node.id,
      targetHandle: 'target',
    };

    nodes.push(node);
    edges.push(newSourceEdge);
    operations.nodes.add!.push(node);
    operations.edges.add!.push(newSourceEdge);

    return { nodes: await tidyNodes(nodes, edges), edges, operations };
  }

  // Scenario 2: Source is connected. Insert new node(s) in between nodes.
  const newSourceEdge: Edge = {
    id: UUID(),
    source: source.nodeId,
    sourceHandle: source.handleId,
    target: node.id,
    targetHandle: 'target',
  };

  const newHandleEdges: Edge[] = edgesConnectedToSource.map((edge) => ({
    id: UUID(),
    source: node.id,
    sourceHandle: 'source',
    target: edge.target,
    targetHandle: 'target',
  }));

  // Remove existing handle edges from edges
  edgesConnectedToSource.forEach((edge) => {
    const existingEdgeIndex = edges.findIndex((e) => e.id === edge.id);
    if (existingEdgeIndex > -1) {
      operations.edges.remove!.push(edges[existingEdgeIndex]!);
      edges.splice(existingEdgeIndex, 1);
    }
  });

  // Update node and edges arrays
  nodes.push(node);
  edges.push(newSourceEdge, ...newHandleEdges);
  operations.nodes.add!.push(node);
  operations.edges.add!.push(newSourceEdge, ...newHandleEdges);

  return { nodes: await tidyNodes(nodes, edges), edges, operations };
};

// Purpose: Function inserts a new node of type PATH
// Restriction: Does not support insertion of APP or AI_BLOCK type nodes. Use 'insertNode' instead.
export const insertPathNode = async ({
  node,
  source,
  ...rest
}: {
  node: PathNode;
  source: NewNodeSource;
  nodes: Node[];
  edges: Edge[];
}) => {
  const nodes = [...rest.nodes];
  const edges = [...rest.edges];

  const operations: NodesChangeset = {
    nodes: { add: [] },
    edges: { add: [], remove: [] },
  };

  const sourceNode = nodes.find((node) => node.id === source.nodeId)!;
  const sourceNodeChildren = getOutgoers(sourceNode, nodes, edges);
  const hasPathNodesAsChildren = sourceNodeChildren.some((node) => node.type === FlowNodeType.PATH);

  // 1. First Scenario: The source node does not have any children
  // Action: We add two path nodes as children of the source node
  if (sourceNodeChildren.length === 0) {
    const additionalPathNode: PathNode = {
      id: UUID(),
      type: FlowNodeType.PATH,
      position: { x: 0, y: 0 },
      data: {},
    };

    const newEdge1: Edge = {
      id: UUID(),
      source: source.nodeId,
      sourceHandle: source.handleId,
      target: node.id,
      targetHandle: 'target',
    };
    const newEdge2: Edge = {
      id: UUID(),
      source: source.nodeId,
      sourceHandle: source.handleId,
      target: additionalPathNode.id,
      targetHandle: 'target',
    };

    nodes.push(node, additionalPathNode);
    edges.push(newEdge1, newEdge2);

    operations.nodes.add!.push(node, additionalPathNode);
    operations.edges.add!.push(newEdge1, newEdge2);

    return { nodes: await tidyNodes(nodes, edges), edges, operations };
  }

  // 2. Second Scenario: The source node has children and there are already path nodes as children
  // Action: We add the new path node as a child of the source node, i.e., a branch
  if (sourceNodeChildren.length > 0 && hasPathNodesAsChildren) {
    const newEdge: Edge = {
      id: UUID(),
      source: source.nodeId,
      sourceHandle: source.handleId,
      target: node.id,
      targetHandle: 'target',
    };

    nodes.push(node);
    edges.push(newEdge);

    operations.nodes.add!.push(node);
    operations.edges.add!.push(newEdge);

    return { nodes: await tidyNodes(nodes, edges), edges, operations };
  }

  const edgeConnectedToHandle = edges.find(
    (edge) => edge.source === source.nodeId && edge.sourceHandle === source.handleId,
  );

  if (!edgeConnectedToHandle) return;

  // 3. Third Scenario: The source node has children and there are no path nodes as children
  // Action: We add the new path node in between the source node and its children
  const additionalPathNode: PathNode = {
    id: UUID(),
    type: FlowNodeType.PATH,
    position: { x: 0, y: 0 },
    data: {},
  };

  const newEdge1: Edge = {
    id: UUID(),
    source: source.nodeId,
    sourceHandle: source.handleId,
    target: node.id,
    targetHandle: 'target',
  };
  const newEdge2: Edge = {
    id: UUID(),
    source: node.id,
    sourceHandle: 'source',
    target: edgeConnectedToHandle.target,
    targetHandle: 'target',
  };

  const newEdge3: Edge = {
    id: UUID(),
    source: source.nodeId,
    sourceHandle: 'source',
    target: additionalPathNode.id,
    targetHandle: 'target',
  };

  const existingEdgeIndex = edges.findIndex((edge) => edge.id === edgeConnectedToHandle.id);

  if (existingEdgeIndex > -1) {
    operations.edges.remove!.push(edges[existingEdgeIndex]!);
    edges.splice(existingEdgeIndex, 1);
  }

  nodes.push(node, additionalPathNode);
  edges.push(newEdge1, newEdge2, newEdge3);
  operations.nodes.add!.push(node, additionalPathNode);
  operations.edges.add!.push(newEdge1, newEdge2, newEdge3);

  return { nodes: await tidyNodes(nodes, edges), edges, operations };
};

export const disconnectOutgoingEdges = (nodeId: string, edges: Edge[]) =>
  edges.filter((edge) => edge.source !== nodeId);

export const classifierConnectionTypeLabel = (connectionType: AiBlockNodeConnectionType) => {
  switch (connectionType) {
    case AiBlockNodeConnectionType.LINEAR:
      return 'Linear';
    case AiBlockNodeConnectionType.BRANCH:
      return 'Multi-action';
    default:
      return '';
  }
};

// This function is used to join (create a connection) between
// the sibling nodes of a given node. It's useful when a node
// is deleted and we don't want to leave any orphan nodes on the canvas.
export const joinSiblingNodes = (deletedNode: Node, nodes: Node[], edges: Edge[]) => {
  const added: Edge[] = [];

  const updated = [deletedNode].reduce((acc, node) => {
    const incomers = getIncomers(node, nodes, edges);
    const outgoers = getOutgoers(node, nodes, edges);
    const connectedEdges = getConnectedEdges([node], edges);

    const remainingEdges = acc.filter((edge) => !connectedEdges.includes(edge));

    const createdEdges: Edge[] = incomers.flatMap(({ id: source }) =>
      outgoers.map(({ id: target }) => ({
        id: UUID(),
        source,
        type: 'default',
        sourceHandle: 'source',
        targetHandle: 'target',
        target,
      })),
    );
    added.push(...createdEdges);

    return [...remainingEdges, ...createdEdges];
  }, edges);

  return { updated, added };
};

export const changeset = (changes: NodesChangeset, versionId: string): FlowNodesChangeset => {
  const { nodes, edges, conditions } = changes;

  const operations: FlowNodesChangeset = {
    nodes: {
      ...(nodes.add && {
        add: nodes.add.map(nodeFromReactFlowToDB(versionId)),
      }),
      ...(nodes.remove && {
        remove: nodes.remove.map(nodeFromReactFlowToDB(versionId)),
      }),
      ...(nodes.update && {
        update: nodes.update.map(nodeFromReactFlowToDB(versionId)),
      }),
    },
    edges: {
      ...(edges.add && {
        add: edges.add.map(edgeFromReactFlowToDB(versionId)),
      }),
      ...(edges.remove && {
        remove: edges.remove.map(edgeFromReactFlowToDB(versionId)),
      }),
    },
    conditions: {
      ...(conditions?.add && {
        add: conditions.add,
      }),
      ...(conditions?.remove && {
        remove: conditions.remove,
      }),
      ...(conditions?.update && {
        update: conditions.update,
      }),
    },
  };

  return operations;
};

export const removeDescendantsOfNode = (nodeId: string, nodes: Node[], edges: Edge[]) => {
  const nodesIdsToRemove: string[] = [];

  function depthFirstDescendantsSearch(currentNodeId: string): void {
    nodesIdsToRemove.push(currentNodeId);

    const currentNode = nodes.find((node) => node.id === currentNodeId);
    if (currentNode) {
      const outgoers = getOutgoers(currentNode, nodes, edges);

      outgoers.forEach((outgoer) => {
        depthFirstDescendantsSearch(outgoer.id);
      });
    }
  }

  const initialNode = nodes.find((node) => node.id === nodeId);
  if (initialNode) {
    const initialOutgoers = getOutgoers(initialNode, nodes, edges);

    initialOutgoers.forEach((initialOutgoer) => {
      depthFirstDescendantsSearch(initialOutgoer.id);
    });
  }

  const updatedNodes = nodes.filter((node) => !nodesIdsToRemove.includes(node.id));

  const updatedEdges = edges.filter(
    (edge) => !nodesIdsToRemove.includes(edge.source) && !nodesIdsToRemove.includes(edge.target),
  );

  return {
    nodes: updatedNodes,
    edges: updatedEdges,
    nodesToRemove: nodes.filter((node) => nodesIdsToRemove.includes(node.id)),
  };
};

export const appAction = (data: AppNodeData, integrations: Record<string, App>) => {
  if (data.type === AppNodeType.TRIGGER) {
    return integrations[data.integration]?.triggers.find((trigger) => trigger.id === data.operationId);
  }
  return integrations[data.integration]?.targets.find((target) => target.id === data.operationId);
};

export const integrationActionByOperationId = (
  integrations: Record<string, App>,
  integration: string,
  operationId: string,
) => {
  const actions = [...(integrations[integration]?.triggers ?? []), ...(integrations[integration]?.targets ?? [])];

  return actions.find((action) => action.id === operationId);
};

export const findEndNodes = (nodes: FlowNode[], edges: FlowEdge[]): FlowNode[] => {
  const nodeIds = new Set(nodes.map((node) => node.id));
  const sourceIds = new Set(edges.map((edge) => edge.sourceId));

  const endNodes = nodes.filter((node) => !sourceIds.has(node.id) && nodeIds.has(node.id));

  return endNodes;
};

// Type guard function to filter out null values and refine the type
export const isNonNullAiBlock = (aiBlock: AiBlock | null): aiBlock is AiBlock => {
  return aiBlock !== null;
};

export const findTriggerNodes = (nodes: FlowNode[]) => {
  const triggerNodes = nodes.filter((node) => (node?.metadata as unknown as AppNodeData)?.type === 'trigger');

  return triggerNodes;
};

const createDynamicTag = (params: DynamicTagParams) => {
  const { prevNodeId, operation, key, image, isDefault } = params;

  return {
    type: DYNAMIC_EDITOR_TAG_EXTENSION_NAME,
    attrs: {
      dynamicTag: {
        id: `${operation}.${prevNodeId}.${key}`,
        name: key,
        image,
        default: isDefault,
      },
    },
  };
};

export const createInlineTextNode = (type: string, text: string) => {
  return { type, text };
};

export const createStandardContentElement = (type: string) => {
  return { type };
};

type DynamicInputContentElement =
  | { type: string }
  | { type: string; text: string }
  | ReturnType<typeof createDynamicTag>;

// TODO: This function currently is limited to merge all contentNodes in one paragraph node.
// Needs to be extended to support multiple paragraph nodes in the future.
export const createDynamicInputContent = (params: CreateDynamicInputContentParams) => {
  const { prevNodeId, content } = params;

  const contentNodes: DynamicInputContentElement[] = content.map((contentElement) => {
    if (contentElement.type === DYNAMIC_EDITOR_TAG_EXTENSION_NAME) {
      const {
        attrs: { dynamicTag },
      } = contentElement;
      return createDynamicTag({
        prevNodeId,
        operation: dynamicTag.operation,
        image: dynamicTag.image,
        key: dynamicTag.key,
        isDefault: true,
      });
    }
    if (contentElement.type === 'text') {
      return createInlineTextNode(contentElement.type, contentElement.text);
    }
    return createStandardContentElement(contentElement.type);
  });

  return {
    type: 'doc',
    content: [
      {
        type: 'paragraph',
        content: contentNodes,
      },
    ],
  };
};

// TODO: Another instance in the codebase where the decision to dumb data into the `metadata` property
// now leads to unnecessary and potatntially dangerous type castings. We should fix that.
const getIconSrcAndAlt = (node: FlowNode | null, aiBlocks: AiBlock[]) => {
  if (!node || (node.type === FlowNodeType.APP && !(node.metadata as unknown as AppNodeData)?.integration))
    return { src: '', alt: '' };

  if (node.type === FlowNodeType.APP) {
    const metadata = node.metadata as unknown as AppNodeData | undefined;
    const integration = metadata?.integration;
    if (!integration) return { src: '', alt: '' };
    const integrationRef = IntegrationsList[integration];
    if (!integrationRef) return { src: '', alt: '' };
    return {
      src: `/imgs/icons/${integrationRef.icon}.svg`,
      alt: `${capitalize(integrationRef.title)} app icon`,
    };
  }

  if (node.type === FlowNodeType.WAIT) {
    return { src: '/imgs/icons/wait-orange.svg', alt: 'Wait icon' };
  }
  const aiBlockData = aiBlocks.find((aiBlock) => aiBlock.id === (node.metadata as unknown as AiBlockData)?.aiBlockId);
  if (!aiBlockData) {
    return { src: '', alt: '' };
  }
  return {
    src: `/imgs/icons/aiblocks/${aiBlockData.type}.svg`,
    alt: `${capitalize(aiBlockData.type)} app icon`,
  };
};

const getAppNamesFromNodes = (nodes: FlowNode[], aiBlocks: AiBlock[]) => {
  return nodes
    .map((node) => {
      if (node.type === FlowNodeType.APP) {
        return capitalize(IntegrationsList[(node?.metadata as unknown as AppNodeData)?.integration]?.title || '');
      }
      const aiBlockType = aiBlocks.find(
        (aiBlock) => aiBlock.id === (node?.metadata as unknown as AiBlockData)?.aiBlockId,
      )?.type;
      return capitalize(`${aiBlockType} AI Block`);
    })
    .join(', ');
};

export const getAppIconDetails = (nodes: FlowNode[], edges: FlowEdge[], aiBlocks: AiBlock[]) => {
  const nonPathNodes = nodes.filter((node) => node.type !== FlowNodeType.PATH);
  const nodeCount = nonPathNodes.length;

  // Trigger
  const triggerNode = findTriggerNodes(nonPathNodes)[0] || null;

  const { src: triggerAppIconSrc, alt: triggerAppIconAlt } = getIconSrcAndAlt(triggerNode, aiBlocks);

  const trigger = {
    node: triggerNode,
    iconSrc: triggerAppIconSrc,
    iconAlt: triggerAppIconAlt,
  };

  // End
  const endNode = (nodeCount >= 3 && findEndNodes(nonPathNodes, edges)[0]) || null;
  const { src: endNodeAppIconSrc, alt: endNodeAppIconAlt } = getIconSrcAndAlt(endNode, aiBlocks);

  const end = {
    node: endNode,
    iconSrc: endNodeAppIconSrc,
    iconAlt: endNodeAppIconAlt,
  };

  // Center
  const centerNodes = nonPathNodes.filter((node) => node.id !== triggerNode?.id && node.id !== endNode?.id);
  const centerNodesCount = centerNodes.length;
  const showCenterNodePlaceholder = centerNodesCount === 0;
  const showCenterNodeApp = centerNodesCount === 1;
  const showCenterNodeCount = centerNodesCount > 1;
  const centerNode = showCenterNodeApp && centerNodes[0] ? centerNodes[0] : null;
  const { src: centerNodeAppIconSrc, alt: centerNodeAppIconAlt } = getIconSrcAndAlt(centerNode, aiBlocks);
  const centerAppNames = getAppNamesFromNodes(centerNodes, aiBlocks);

  const center = {
    node: centerNode,
    count: centerNodesCount,
    iconSrc: centerNodeAppIconSrc,
    iconAlt: centerNodeAppIconAlt,
    tooltip: { content: { description: centerAppNames } },
    showPlaceholder: showCenterNodePlaceholder,
    showCount: showCenterNodeCount,
    showApp: showCenterNodeApp,
  };

  return { trigger, center, end };
};

export function buildTreeFromFlow<T extends { id: string }, U>(
  nodes: Pick<T, 'id'>[],
  edges: Pick<FlowEdge, 'sourceId' | 'targetId'>[],
  value?: (id: string) => U,
) {
  const nodesMap = new Map<string, FlowTreeNode<T & U>>();
  nodes.forEach((node) => {
    const callbackResult = value ? value(node.id) : {};
    const nodeData = { ...node, ...callbackResult, children: [] };
    nodesMap.set(node.id, nodeData as FlowTreeNode<T & U>);
  });

  const rootCandidates = new Set(nodes.map((node) => node.id));

  edges.forEach((edge) => {
    const parent = nodesMap.get(edge.sourceId);
    const child = nodesMap.get(edge.targetId);
    if (parent && child) {
      parent.children = parent.children || [];
      parent.children.push(child);
      rootCandidates.delete(edge.targetId);
    }
  });

  if (rootCandidates.size === 1) {
    const rootId = rootCandidates.values().next().value!;
    return nodesMap.get(rootId) || null;
  }

  throw new Error('Multiple root nodes found or no root node!');
}

const removeSourceNodeReferences = (conditions: FlowPathCondition[], nodeId: string) => {
  return conditions.map((condition) => {
    if (condition.sourceNodeId === nodeId) {
      return {
        ...condition,
        sourceNodeId: null,
        sourceOutputKey: null,
      };
    }
    return condition;
  });
};

export const addNodesToFlowCache = (
  previousData: Awaited<ReturnType<typeof getOne>>,
  nodes: FlowNodeWithoutConnection[],
) => {
  const nextFlowData = produce(previousData, (draft) => {
    nodes.forEach((node) => {
      draft.flow.nodes.push({
        id: node.id,
        type: node.type,
        nodeInputs: [],
        flowPaths: [],
        versionId: draft.versionId!,
        connectionId: null,
        metadata: node.metadata,
        flowNodeCustomCode: null,
      });
    });
  });

  return nextFlowData;
};

export const updateFlowCache = (previousData: Awaited<ReturnType<typeof getOne>>, changeset: FlowNodesChangeset) => {
  const { nodes, edges, conditions } = changeset;

  const nextFlowData = produce(previousData, (draft) => {
    nodes.update?.forEach((node) => {
      const index = draft.flow.nodes.findIndex((n) => n.id === node.id);
      if (index !== -1) {
        const existingNode = draft.flow.nodes[index]!;

        draft.flow.nodes[index] = {
          ...existingNode,
          type: node.type as FlowNodeType,
          metadata: node.metadata,
        };

        const isAReplacedTrigger =
          typeof node.metadata === 'object' &&
          node.metadata !== null &&
          'type' in node.metadata &&
          'shouldRemoveRelatedNodeInputs' in node.metadata &&
          node.metadata.type === AppNodeType.TRIGGER &&
          node.metadata.shouldRemoveRelatedNodeInputs;

        if (isAReplacedTrigger) {
          draft.flow.nodes.forEach((n) => {
            n.flowPaths.forEach((flowPath) => {
              flowPath.conditionGroups.forEach((cg) => {
                cg.conditions = removeSourceNodeReferences(cg.conditions, node.id);
              });
            });
          });
        }
      }
    });

    edges.add?.forEach((edge) => {
      draft.flow.edges.push({
        versionId: draft.versionId!,
        sourceId: edge.sourceId,
        targetId: edge.targetId,
        id: edge.id,
        sourceHandle: edge.sourceHandle!,
        targetHandle: edge.targetHandle!,
      });
    });

    edges.remove?.forEach((edge) => {
      draft.flow.edges = draft.flow.edges.filter((e) => e.id !== edge.id);
    });

    conditions?.add?.forEach((condition) => {
      draft.flow.nodes.forEach((node) => {
        node.flowPaths.forEach((flowPath) => {
          flowPath.conditionGroups.forEach((cg) => {
            if (cg.id === condition.conditionGroupId) cg.conditions.push(condition);
          });
        });
      });
    });

    conditions?.remove?.forEach((conditionId) => {
      draft.flow.nodes.forEach((node) => {
        node.flowPaths.forEach((flowPath) => {
          flowPath.conditionGroups.forEach((cg: { conditions: { id: string }[] }) => {
            cg.conditions = cg.conditions.filter((c) => c.id !== conditionId);
          });
        });
      });
    });

    conditions?.update?.forEach((condition) => {
      draft.flow.nodes.forEach((node) => {
        node.flowPaths.forEach((flowPath) => {
          flowPath.conditionGroups.forEach((cg) => {
            cg.conditions = cg.conditions.map((c) =>
              c.id === condition.id ? ({ ...cg, ...condition } as FlowPathCondition) : c,
            );
          });
        });
      });
    });
  });

  return nextFlowData;
};

export const deleteNodesFromFlowCache = (previousData: Awaited<ReturnType<typeof getOne>>, nodeIds: string[]) => {
  if (!nodeIds || nodeIds.length === 0) return previousData;

  const removeNodeIds = new Set(nodeIds);

  const nextFlowData = produce(previousData, (draft) => {
    draft.flow.nodes = draft.flow.nodes.filter((node) => !removeNodeIds.has(node.id));

    removeNodeIds.forEach((removedNodeId) => {
      draft.flow.nodes.forEach((node) => {
        node.flowPaths.forEach((flowPath) => {
          flowPath.conditionGroups.forEach((cg) => {
            cg.conditions = removeSourceNodeReferences(cg.conditions, removedNodeId);
          });
        });
      });
    });
  });

  return nextFlowData;
};

export const replaceFlowNodes = (previousData: { flow: Flow & { nodes: FlowNode[] } }, updatedNodes: FlowNode[]) => {
  const nextFlowData = produce(previousData, (draft: { flow: { nodes: { id: string }[] } }) => {
    updatedNodes.forEach((node) => {
      const index = draft.flow.nodes.findIndex((n) => n.id === node.id);
      if (index !== -1) {
        draft.flow.nodes[index] = node;
      }
    });
  });

  return nextFlowData;
};

export const aiBlockOperationId = (type: AiBlockType) => {
  switch (type) {
    case AiBlockType.classify:
      return 'llm-classify-with-block-labels';
    case AiBlockType.extract:
      return 'llm-extract-text';
    case AiBlockType.generate:
      return 'llm-generate-text';
    case AiBlockType.summarize:
      return 'llm-summarize-text';
    case AiBlockType.custom:
      return 'llm-custom-ai-text';
    default:
      return null;
  }
};

export const lastNodes = (nodes: FlowNode[], edges: FlowEdge[], stopAt?: string) => {
  // Create a map of node ids to their outgoing edges count
  const outgoingCount = new Map<string, number>();

  edges.forEach((edge) => {
    outgoingCount.set(edge.sourceId, (outgoingCount.get(edge.sourceId) || 0) + 1);
  });

  // Last nodes are those for which the outgoingCount is 0
  // or the node with the id that matches the stopAt parameter
  return nodes.filter((node) => (outgoingCount.get(node.id) || 0) <= 0 || stopAt === node.id);
};
